import { loadBaseCSS } from '../shared/button-utils.js';
import {
  renderTrashIcon,
  renderVisibilityIcon
} from '../content-blocks/qds-content-icons.js';
import {
  clearActiveContentDragHover,
  clearActiveContentDragState,
  getActiveContentDragHover,
  getActiveContentDragPayload,
  getContentDragPayload,
  setActiveContentDragHover,
  setContentDragPayload
} from '../content-blocks/qds-content-drag.js';
import {
  blockSupportsChildren,
  escapeHtml,
  getBlockChildOptions,
  getBlockChildren,
  getContentBlockDisplayName,
  normalizeContentDocument,
  resolveRegistry,
  sortBlocksForMode
} from '../content-blocks/qds-content-blocks.js';
import type { QdsContentDocument } from '../../types/content.js';
import type { QdsBlockRegistry } from '../../types/blocks.js';
import type { QdsBlockDropDetail, QdsBlockDropTarget } from '../../types/designer.js';

loadBaseCSS();

const ROOT_PARENT_ID = '__root__';

type OutlineAction =
  | 'select-block'
  | 'duplicate-block'
  | 'remove-block'
  | 'toggle-visibility';

interface ResolvedDropDetail {
  signature: string;
  target: QdsBlockDropTarget;
  kind: 'before' | 'after' | 'tail' | 'empty';
  element: HTMLElement;
}

let outlineDragOwnerCounter = 0;

function encodeParentBlockId(parentBlockId: string | null): string {
  return parentBlockId || ROOT_PARENT_ID;
}

function decodeParentBlockId(value: string | null | undefined): string | null {
  return value && value !== ROOT_PARENT_ID ? value : null;
}

function toIndex(value: string | null | undefined): number | null {
  const parsed = Number.parseInt(value || '', 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function containsPoint(element: HTMLElement, clientX: number, clientY: number): boolean {
  const rect = element.getBoundingClientRect();
  return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
}

function getDepth(element: HTMLElement): number {
  return Number.parseInt(element.dataset.depth || '0', 10) || 0;
}

function getArea(element: HTMLElement): number {
  const rect = element.getBoundingClientRect();
  return rect.width * rect.height;
}

/**
 * @element qds-content-outline
 *
 * A tree view of the content document's nested block structure, supporting selection, visibility toggling, duplication, removal, and drag-and-drop reordering of blocks.
 *
 * @attr {boolean} readonly - When present, disables outline editing (drag, duplicate, remove, visibility)
 * @attr {string} selected-block-id - Id of the block currently selected in the outline
 *
 * @property {QdsContentDocument | null} document - The content document whose structure is displayed
 * @property {QdsBlockRegistry | null} registry - The block registry used to resolve block definitions
 * @property {string | null} selectedBlockId - Id of the currently selected block
 * @property {boolean} readonly - Whether outline editing is disabled
 *
 * @fires block-select - Fired when a block is selected in the outline
 * @fires block-duplicate - Fired when a block's duplicate action is triggered
 * @fires block-remove - Fired when a block's remove action is triggered
 * @fires block-visibility-toggle - Fired when a block's visibility is toggled
 * @fires block-drop - Fired when a block is dropped onto a resolved target, carrying the drag item and target location
 */
export class QdsContentOutline extends HTMLElement {
  private _document: QdsContentDocument | null = null;
  private _registry: QdsBlockRegistry | null = null;
  private _selectedBlockId: string | null = null;
  private _readonly = false;
  private _activeDropDetail: ResolvedDropDetail | null = null;
  private readonly _dragOwnerId = `outline-${++outlineDragOwnerCounter}`;

  static get observedAttributes(): string[] {
    return ['readonly', 'selected-block-id'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    if (!this.isConnected || !this.shadowRoot) {
      return;
    }

    this.shadowRoot.addEventListener('click', this._handleClick);
    this.shadowRoot.addEventListener('dragstart', this._handleDragStart as EventListener);
    this.shadowRoot.addEventListener('dragend', this._handleDragEnd as EventListener);
    this.shadowRoot.addEventListener('dragover', this._handleDragOver as EventListener);
    this.shadowRoot.addEventListener('drop', this._handleDrop as EventListener);
    this.render();
    window.addEventListener('dragend', this._handleGlobalDragEnd as EventListener);
    window.addEventListener('drop', this._handleGlobalDragEnd as EventListener);
  }

  disconnectedCallback(): void {
    this.shadowRoot?.removeEventListener('click', this._handleClick);
    this.shadowRoot?.removeEventListener('dragstart', this._handleDragStart as EventListener);
    this.shadowRoot?.removeEventListener('dragend', this._handleDragEnd as EventListener);
    this.shadowRoot?.removeEventListener('dragover', this._handleDragOver as EventListener);
    this.shadowRoot?.removeEventListener('drop', this._handleDrop as EventListener);
    window.removeEventListener('dragend', this._handleGlobalDragEnd as EventListener);
    window.removeEventListener('drop', this._handleGlobalDragEnd as EventListener);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    if (name === 'readonly') {
      this._readonly = newValue !== null;
    }

    if (name === 'selected-block-id') {
      this._selectedBlockId = newValue;
    }

    this.render();
  }

  get document(): QdsContentDocument | null {
    return this._document;
  }

  set document(value: QdsContentDocument | null) {
    this._document = value;
    this.render();
  }

  get registry(): QdsBlockRegistry | null {
    return this._registry;
  }

  set registry(value: QdsBlockRegistry | null) {
    this._registry = value;
    this.render();
  }

  get selectedBlockId(): string | null {
    return this._selectedBlockId;
  }

  set selectedBlockId(value: string | null) {
    this._selectedBlockId = value;
    if (value) {
      this.setAttribute('selected-block-id', value);
    } else {
      this.removeAttribute('selected-block-id');
    }
  }

  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    if (this._readonly) {
      this.setAttribute('readonly', '');
    } else {
      this.removeAttribute('readonly');
    }
  }

  private _dispatch(name: string, detail: Record<string, unknown>): void {
    this.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      composed: true,
      detail
    }));
  }

  private _pickDeepestTarget(selector: string, clientX: number, clientY: number): HTMLElement | null {
    const elements = Array.from(this.shadowRoot?.querySelectorAll<HTMLElement>(selector) || [])
      .filter((element) => containsPoint(element, clientX, clientY))
      .sort((left, right) => {
        const depthDelta = getDepth(right) - getDepth(left);
        if (depthDelta !== 0) {
          return depthDelta;
        }

        return getArea(left) - getArea(right);
      });

    return elements[0] || null;
  }

  private _resolveDropDetail(
    clientX: number,
    clientY: number,
    eventTarget?: EventTarget | null
  ): ResolvedDropDetail | null {
    const targetElement = eventTarget instanceof HTMLElement ? eventTarget : null;
    if (targetElement) {
      const slot = targetElement.closest<HTMLElement>('[data-slot-parent-id][data-slot-index]');
      if (slot) {
        const parentBlockId = decodeParentBlockId(slot.dataset.slotParentId);
        const index = toIndex(slot.dataset.slotIndex);
        if (index !== null) {
          const rect = slot.getBoundingClientRect();
          const insertBefore = clientY <= rect.top + (rect.height / 2);
          return {
            signature: `slot:${encodeParentBlockId(parentBlockId)}:${index}:${insertBefore ? 'before' : 'after'}`,
            target: {
              parentBlockId,
              index: insertBefore ? index : index + 1
            },
            kind: insertBefore ? 'before' : 'after',
            element: slot
          };
        }
      }

      const tail = targetElement.closest<HTMLElement>('[data-tail-parent-id][data-tail-index]');
      if (tail) {
        const parentBlockId = decodeParentBlockId(tail.dataset.tailParentId);
        const index = toIndex(tail.dataset.tailIndex);
        if (index !== null) {
          return {
            signature: `tail:${encodeParentBlockId(parentBlockId)}:${index}`,
            target: {
              parentBlockId,
              index
            },
            kind: 'tail',
            element: tail
          };
        }
      }

      const empty = targetElement.closest<HTMLElement>('[data-empty-parent-id][data-empty-index]');
      if (empty) {
        const parentBlockId = decodeParentBlockId(empty.dataset.emptyParentId);
        const index = toIndex(empty.dataset.emptyIndex);
        if (index !== null) {
          return {
            signature: `empty:${encodeParentBlockId(parentBlockId)}:${index}`,
            target: {
              parentBlockId,
              index
            },
            kind: 'empty',
            element: empty
          };
        }
      }
    }

    const slot = this._pickDeepestTarget('[data-slot-parent-id][data-slot-index]', clientX, clientY);
    if (slot) {
      const parentBlockId = decodeParentBlockId(slot.dataset.slotParentId);
      const index = toIndex(slot.dataset.slotIndex);
      if (index !== null) {
        const rect = slot.getBoundingClientRect();
        const insertBefore = clientY <= rect.top + (rect.height / 2);
        return {
          signature: `slot:${encodeParentBlockId(parentBlockId)}:${index}:${insertBefore ? 'before' : 'after'}`,
          target: {
            parentBlockId,
            index: insertBefore ? index : index + 1
          },
          kind: insertBefore ? 'before' : 'after',
          element: slot
        };
      }
    }

    const tail = this._pickDeepestTarget('[data-tail-parent-id][data-tail-index]', clientX, clientY);
    if (tail) {
      const parentBlockId = decodeParentBlockId(tail.dataset.tailParentId);
      const index = toIndex(tail.dataset.tailIndex);
      if (index !== null) {
        return {
          signature: `tail:${encodeParentBlockId(parentBlockId)}:${index}`,
          target: {
            parentBlockId,
            index
          },
          kind: 'tail',
          element: tail
        };
      }
    }

    const empty = this._pickDeepestTarget('[data-empty-parent-id][data-empty-index]', clientX, clientY);
    if (empty) {
      const parentBlockId = decodeParentBlockId(empty.dataset.emptyParentId);
      const index = toIndex(empty.dataset.emptyIndex);
      if (index !== null) {
        return {
          signature: `empty:${encodeParentBlockId(parentBlockId)}:${index}`,
          target: {
            parentBlockId,
            index
          },
          kind: 'empty',
          element: empty
        };
      }
    }

    return null;
  }

  private _setActiveDropDetail(nextDetail: ResolvedDropDetail | null): void {
    if (this._activeDropDetail?.signature === nextDetail?.signature) {
      return;
    }

    if (this._activeDropDetail) {
      if (this._activeDropDetail.kind === 'before') {
        this._activeDropDetail.element.classList.remove('outline__item--drop-before');
      } else if (this._activeDropDetail.kind === 'after') {
        this._activeDropDetail.element.classList.remove('outline__item--drop-after');
      } else {
        this._activeDropDetail.element.classList.remove('outline__drop-zone--active');
      }
    }

    this._activeDropDetail = nextDetail;

    if (!nextDetail) {
      clearActiveContentDragHover(this._dragOwnerId);
      this.removeAttribute('drag-active');
      return;
    }

    if (nextDetail.kind === 'before') {
      nextDetail.element.classList.add('outline__item--drop-before');
    } else if (nextDetail.kind === 'after') {
      nextDetail.element.classList.add('outline__item--drop-after');
    } else {
      nextDetail.element.classList.add('outline__drop-zone--active');
    }

    setActiveContentDragHover(this._dragOwnerId, nextDetail.target);
    this.setAttribute('drag-active', '');
  }

  private _handleClick = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    const actionElement = target?.closest<HTMLElement>('[data-action]');

    if (!actionElement) {
      return;
    }

    const action = actionElement.dataset.action as OutlineAction;
    const blockId = actionElement.dataset.blockId;

    if (!blockId) {
      return;
    }

    if (action === 'select-block') {
      this._dispatch('block-select', { blockId });
      return;
    }

    if (this._readonly) {
      return;
    }

    switch (action) {
      case 'duplicate-block':
        this._dispatch('block-duplicate', { blockId });
        break;
      case 'remove-block':
        this._dispatch('block-remove', { blockId });
        break;
      case 'toggle-visibility':
        this._dispatch('block-visibility-toggle', { blockId });
        break;
      default:
        break;
    }
  };

  private _handleDragStart = (event: DragEvent): void => {
    if (this._readonly || !this._document) {
      event.preventDefault();
      return;
    }

    const documentModel = normalizeContentDocument(this._document, resolveRegistry(this._registry));
    if (documentModel.mode !== 'page') {
      event.preventDefault();
      return;
    }

    const target = event.target as HTMLElement | null;
    const item = target?.closest<HTMLElement>('[data-draggable-block-id]');
    const blockId = item?.dataset.draggableBlockId;

    if (!item || !blockId || !setContentDragPayload(event, {
      kind: 'document-block',
      blockId,
      mode: documentModel.mode
    })) {
      event.preventDefault();
      return;
    }

    item.classList.add('outline__item--dragging');
    this._dispatch('block-select', { blockId });
  };

  private _handleDragEnd = (): void => {
    this.shadowRoot?.querySelectorAll('.outline__item--dragging').forEach((element) => {
      element.classList.remove('outline__item--dragging');
    });
    this._setActiveDropDetail(null);
  };

  private _handleDragOver = (event: DragEvent): void => {
    if (this._readonly || !event.dataTransfer) {
      return;
    }

    const payload = getContentDragPayload(event) || getActiveContentDragPayload();
    if (!payload) {
      return;
    }

    const detail = this._resolveDropDetail(event.clientX, event.clientY, event.target);
    if (!detail) {
      this._setActiveDropDetail(null);
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = payload.kind === 'palette-block' ? 'copy' : 'move';
    this._setActiveDropDetail(detail);
  };

  private _handleDrop = (event: DragEvent): void => {
    if (this._readonly) {
      return;
    }

    const payload = getContentDragPayload(event) || getActiveContentDragPayload();
    const detail = this._resolveDropDetail(event.clientX, event.clientY, event.target);
    this._setActiveDropDetail(null);

    if (!payload || !detail) {
      return;
    }

    event.preventDefault();
    clearActiveContentDragState();
    this._dispatch('block-drop', {
      item: payload.kind === 'palette-block'
        ? { kind: 'new', type: payload.type }
        : { kind: 'existing', blockId: payload.blockId },
      target: detail.target
    } satisfies QdsBlockDropDetail);
  };

  private _handleGlobalDragEnd = (): void => {
    const hover = getActiveContentDragHover();
    const payload = getActiveContentDragPayload();
    if (hover?.ownerId === this._dragOwnerId && payload) {
      this._dispatch('block-drop', {
        item: payload.kind === 'palette-block'
          ? { kind: 'new', type: payload.type }
          : { kind: 'existing', blockId: payload.blockId },
        target: hover.target
      } satisfies QdsBlockDropDetail);
      clearActiveContentDragState();
    }

    this._handleDragEnd();
  };

  private _renderBranch(
    blocks: QdsContentDocument['blocks'],
    parentBlockId: string | null,
    depth: number,
    registry: QdsBlockRegistry
  ): string {
    const orderedBlocks = sortBlocksForMode(blocks, 'page');
    const encodedParentId = encodeParentBlockId(parentBlockId);

    if (orderedBlocks.length === 0) {
      return `
        <div
          class="outline__empty ${parentBlockId ? 'outline__empty--nested' : ''}"
          data-empty-parent-id="${encodedParentId}"
          data-empty-index="0"
          data-depth="${depth}"
        >
          ${parentBlockId ? 'Drop blocks here.' : 'Drag a block here from the palette to start the page flow.'}
        </div>
      `;
    }

    return `
      <div class="outline__list outline__list--depth-${depth}">
        ${orderedBlocks.map((block, index) => this._renderItem(block, parentBlockId, index, depth, registry)).join('')}
        <div
          class="outline__tail"
          data-tail-parent-id="${encodedParentId}"
          data-tail-index="${orderedBlocks.length}"
          data-depth="${depth}"
        >
          Drop here to place at the end.
        </div>
      </div>
    `;
  }

  private _renderItem(
    block: QdsContentDocument['blocks'][number],
    parentBlockId: string | null,
    index: number,
    depth: number,
    registry: QdsBlockRegistry
  ): string {
    const selectedClass = block.id === this._selectedBlockId ? 'outline__item outline__item--selected' : 'outline__item';
    const visibilityLabel = block.visibility?.hidden ? 'Hidden' : 'Visible';
    const supportsChildren = blockSupportsChildren(block, registry);
    const childOptions = supportsChildren ? getBlockChildOptions(registry.get(block.type)) : undefined;

    return `
      <article
        class="${selectedClass} ${!this._readonly ? 'outline__item--draggable' : ''}"
        data-slot-parent-id="${encodeParentBlockId(parentBlockId)}"
        data-slot-index="${index}"
        data-depth="${depth}"
        data-draggable-block-id="${block.id}"
        draggable="${!this._readonly ? 'true' : 'false'}"
      >
        <button type="button" class="outline__select" data-action="select-block" data-block-id="${block.id}">
          <span class="outline__label">${escapeHtml(getContentBlockDisplayName(block, registry))}</span>
          <span class="outline__meta">${escapeHtml(block.type)} · ${visibilityLabel}</span>
        </button>
        <div class="outline__actions">
          <button
            type="button"
            class="outline__action"
            data-action="toggle-visibility"
            data-block-id="${block.id}"
            aria-label="${block.visibility?.hidden ? 'Show block' : 'Hide block'}"
            title="${block.visibility?.hidden ? 'Show block' : 'Hide block'}"
            ${this._readonly ? 'disabled' : ''}
          >
            ${renderVisibilityIcon(Boolean(block.visibility?.hidden))}
          </button>
          <button
            type="button"
            class="outline__action"
            data-action="remove-block"
            data-block-id="${block.id}"
            aria-label="Delete block"
            title="Delete block"
            ${this._readonly ? 'disabled' : ''}
          >
            ${renderTrashIcon()}
          </button>
        </div>
        ${supportsChildren ? `
          <div class="outline__branch outline__branch--${childOptions?.layout || 'flow'}">
            ${this._renderBranch(getBlockChildren(block, 'page'), block.id, depth + 1, registry)}
          </div>
        ` : ''}
      </article>
    `;
  }

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    if (!this._document) {
      this.shadowRoot.innerHTML = `
        <style>
          :host {
            display: block;
            font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
          }
        </style>
        <div>No document is loaded.</div>
      `;
      return;
    }

    const registry = resolveRegistry(this._registry);
    const documentModel = normalizeContentDocument(this._document, registry);

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          color: var(--theme-text-primary, #333333);
          font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
        }

        .outline {
          display: grid;
          gap: 16px;
        }

        .outline__header {
          display: grid;
          gap: 8px;
        }

        .outline__title {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .outline__description {
          margin: 0;
          color: var(--theme-text-secondary, #5f6368);
        }

        .outline__list {
          display: grid;
          gap: 8px;
        }

        .outline__item {
          position: relative;
          display: grid;
          gap: 10px;
          padding: 12px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 8px;
          background: var(--theme-background-primary, #ffffff);
        }

        .outline__item--draggable {
          cursor: grab;
        }

        .outline__item--dragging {
          opacity: 0.55;
        }

        .outline__item--drop-before::before,
        .outline__item--drop-after::after {
          content: '';
          position: absolute;
          left: 8px;
          right: 8px;
          height: 3px;
          border-radius: 999px;
          background: var(--input-focus-border, #4285f4);
          box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.14);
          pointer-events: none;
        }

        .outline__item--drop-before::before {
          top: -2px;
        }

        .outline__item--drop-after::after {
          bottom: -2px;
        }

        .outline__item--selected {
          border-color: var(--input-focus-border, #4285f4);
          box-shadow: var(--shadow-focus, 0 1px 6px rgba(32, 33, 36, 0.2));
        }

        .outline__select {
          display: grid;
          gap: 4px;
          width: 100%;
          padding: 0;
          border: none;
          background: transparent;
          text-align: left;
          color: inherit;
          cursor: pointer;
        }

        .outline__label {
          font-size: 14px;
          font-weight: 600;
        }

        .outline__meta {
          color: var(--theme-text-secondary, #5f6368);
          font-size: 12px;
        }

        .outline__actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .outline__action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          padding: 0;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 999px;
          background: var(--theme-background-secondary, #f8f8f8);
          color: inherit;
          cursor: ${this._readonly ? 'default' : 'pointer'};
        }

        .outline__action svg {
          width: 14px;
          height: 14px;
        }

        .outline__branch {
          display: grid;
          gap: 8px;
          padding-left: 16px;
          border-left: 1px solid rgba(0, 0, 0, 0.08);
        }

        .outline__branch--grid .outline__list {
          gap: 10px;
        }

        .outline__action:focus-visible,
        .outline__select:focus-visible {
          outline: 2px solid var(--input-focus-border, #4285f4);
          outline-offset: 2px;
        }

        .outline__empty,
        .outline__tail {
          min-height: 32px;
          padding: 12px;
          border: 1px dashed transparent;
          border-radius: 10px;
          color: transparent;
          transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
        }

        .outline__empty {
          border-color: var(--theme-border-color, #e0e0e0);
          background: var(--theme-background-secondary, #f8f8f8);
          color: var(--theme-text-secondary, #5f6368);
        }

        :host([drag-active]) .outline__tail {
          border-color: rgba(66, 133, 244, 0.28);
          background: rgba(66, 133, 244, 0.04);
          color: var(--theme-text-secondary, #5f6368);
        }

        .outline__drop-zone--active {
          border-color: var(--input-focus-border, #4285f4) !important;
          background: rgba(66, 133, 244, 0.12) !important;
          color: var(--theme-text-secondary, #5f6368) !important;
        }
      </style>
      <section class="outline">
        <header class="outline__header">
          <h2 class="outline__title">Outline</h2>
          <p class="outline__description">
            ${documentModel.mode === 'page'
              ? 'Nested page structure. Drag between items to reorder or move blocks between containers.'
              : 'Blocks currently in the document.'}
          </p>
        </header>
        ${documentModel.mode === 'page'
          ? this._renderBranch(documentModel.blocks, null, 0, registry)
          : this._renderBranch(documentModel.blocks, null, 0, registry)}
      </section>
    `;
  }
}

if (!customElements.get('qds-content-outline')) {
  customElements.define('qds-content-outline', QdsContentOutline);
}

export default QdsContentOutline;
